import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  RiCalendarLine,
  RiMapPinLine,
  RiTimeLine,
  RiCheckLine,
  RiCalendarEventLine,
  RiArrowRightLine,
  RiUserStarLine,
  RiGlobalLine,
  RiAwardLine,
} from 'react-icons/ri';
import { API_BASE } from '../config';

/* ─── THEME COLORS (Blue & White) ───────────────────────────────────────────── */
const T = {
  white: "#FFFFFF",
  offWhite: "#F8FAFC",
  softBlue: "#EFF6FF",
  primary: "#1A56DB",
  primaryDark: "#1240A8",
  primaryLight: "#3B82F6",
  navy: "#0F2557",
  navyDeep: "#091A3E",
  accent: "#60A5FA",
  accentGold: "#F59E0B",
  text: "#1E293B",
  textMid: "#475569",
  textMuted: "#64748B",
  border: "#E2E8F0",
  borderLight: "#F1F5F9",
  shadow: "0 4px 20px rgba(0,0,0,0.05)",
  shadowMd: "0 8px 30px rgba(0,0,0,0.08)",
  shadowLg: "0 20px 40px rgba(0,0,0,0.1)",
  gradPrimary: "linear-gradient(135deg, #1A56DB 0%, #3B82F6 100%)",
  gradNavy: "linear-gradient(135deg, #0F2557 0%, #1A56DB 100%)",
  gradSoft: "linear-gradient(135deg, #EFF6FF 0%, #F8FAFC 100%)",
  gradGold: "linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)",
};

/* ─── SECTION LABEL ──────────────────────────────────────────────────────────*/
function SectionLabel({ label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
      <span style={{
        fontSize: 11,
        fontWeight: 700,
        color: T.primary,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}>
        {label}
      </span>
      <div style={{ flex: 1, height: 1, background: T.borderLight }} />
    </div>
  );
}

/* ─── SESSION CARD ───────────────────────────────────────────────────────────*/
function SessionCard({ session, index, isCompleted }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.06 }}
      whileHover={{ y: -4, borderColor: isCompleted ? T.success : T.primary }}
      style={{
        background: T.white,
        border: `1px solid ${T.borderLight}`,
        borderRadius: 20,
        padding: '28px 28px',
        transition: 'all 0.25s ease',
        cursor: 'default',
        boxShadow: T.shadow,
      }}
    >
      {/* Top row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {/* Time badge */}
          {(session.time || session.date) && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: isCompleted ? 'rgba(16,185,129,0.1)' : T.softBlue,
              border: `1px solid ${isCompleted ? 'rgba(16,185,129,0.25)' : T.borderLight}`,
              padding: '5px 14px',
              borderRadius: 30,
              color: isCompleted ? T.success : T.primary,
              fontSize: 13,
              fontWeight: 600,
            }}>
              <RiTimeLine size={13} />
              {session.time || session.date}
            </div>
          )}

          {/* Location badge */}
          {(session.room || session.location) && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: `${T.accent}10`,
              border: `1px solid ${T.borderLight}`,
              padding: '5px 14px',
              borderRadius: 30,
              color: T.accent,
              fontSize: 13,
              fontWeight: 500,
            }}>
              <RiMapPinLine size={13} />
              {session.room || session.location}
            </div>
          )}
        </div>

        {/* Completed badge */}
        {isCompleted && (
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            background: 'rgba(16,185,129,0.1)',
            border: '1px solid rgba(16,185,129,0.25)',
            padding: '4px 12px',
            borderRadius: 30,
            color: T.success,
            fontSize: 12,
            fontWeight: 600,
          }}>
            <RiCheckLine size={13} />
            Completed
          </div>
        )}
      </div>

      {/* Title */}
      <h3 style={{
        fontFamily: "'Playfair Display', serif",
        fontSize: 20,
        fontWeight: 800,
        color: T.text,
        marginBottom: 10,
        lineHeight: 1.3,
      }}>
        {session.title}
      </h3>

      {/* Description */}
      {session.description && (
        <p style={{ fontSize: 14, color: T.textMid, lineHeight: 1.7, marginBottom: 18 }}>
          {session.description}
        </p>
      )}

      {/* Speaker */}
      {(session.speaker || session.speakers?.[0]?.name) && (
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          background: T.softBlue,
          padding: '6px 14px',
          borderRadius: 30,
        }}>
          <div style={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            background: T.gradPrimary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 11,
            fontWeight: 700,
            color: T.white,
          }}>
            {(session.speaker || session.speakers[0].name).charAt(0)}
          </div>
          <span style={{ fontSize: 13, fontWeight: 600, color: T.primary }}>
            {session.speaker || session.speakers[0].name}
          </span>
        </div>
      )}
    </motion.div>
  );
}

/* ─── EMPTY STATE ────────────────────────────────────────────────────────────*/
function EmptyState({ isCompleted }) {
  return (
    <div style={{
      background: T.white,
      border: `1px solid ${T.borderLight}`,
      borderRadius: 20,
      padding: '56px 24px',
      textAlign: 'center',
      boxShadow: T.shadow,
    }}>
      <div style={{
        width: 56,
        height: 56,
        borderRadius: '50%',
        background: T.softBlue,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 16px',
      }}>
        <RiCalendarEventLine size={24} color={T.primary} />
      </div>
      <p style={{ fontSize: 15, color: T.textMuted }}>
        {isCompleted ? 'No completed sessions yet.' : 'No upcoming sessions scheduled.'}
      </p>
    </div>
  );
}

/* ─── SKELETON LOADER ────────────────────────────────────────────────────────*/
function SkeletonCard() {
  return (
    <div style={{
      background: T.white,
      border: `1px solid ${T.borderLight}`,
      borderRadius: 20,
      padding: '28px',
    }}>
      <div style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
        {[80, 100].map((w, i) => (
          <div key={i} style={{
            width: w,
            height: 28,
            borderRadius: 30,
            background: T.borderLight,
            animation: 'pulse 1.5s ease-in-out infinite',
          }} />
        ))}
      </div>
      <div style={{ height: 22, width: '60%', background: T.borderLight, borderRadius: 6, marginBottom: 10 }} />
      <div style={{ height: 14, width: '90%', background: T.borderLight, borderRadius: 6, marginBottom: 6 }} />
      <div style={{ height: 14, width: '75%', background: T.borderLight, borderRadius: 6, marginBottom: 18 }} />
      <div style={{ height: 32, width: 120, background: T.borderLight, borderRadius: 30 }} />
      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }`}</style>
    </div>
  );
}

/* ─── STAT CHIP ──────────────────────────────────────────────────────────────*/
function StatChip({ icon: Icon, label, value }) {
  return (
    <div style={{
      background: T.white,
      border: `1px solid ${T.borderLight}`,
      borderRadius: 16,
      padding: '20px',
      textAlign: 'center',
      boxShadow: T.shadow,
      transition: 'all 0.2s',
    }}
    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = T.shadowMd; }}
    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = T.shadow; }}>
      <div style={{
        width: 40,
        height: 40,
        borderRadius: 12,
        background: T.softBlue,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 12px',
      }}>
        <Icon size={18} color={T.primary} />
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: T.primary, fontFamily: "'Playfair Display', serif", marginBottom: 4 }}>
        {value}
      </div>
      <div style={{ fontSize: 12, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        {label}
      </div>
    </div>
  );
}

/* ─── MAIN COMPONENT ─────────────────────────────────────────────────────────*/
export default function Agenda() {
  const [summits, setSummits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');

  useEffect(() => {
    fetchSummits();
  }, []);

  const fetchSummits = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/summits`);
      const data = await response.json();
      setSummits(data.summits || data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const upcoming = summits.filter(s => s.status === 'upcoming' || !s.status);
  const completed = summits.filter(s => s.status === 'completed');

  const TabBtn = ({ id, label, count }) => (
    <button
      onClick={() => setActiveTab(id)}
      style={{
        padding: '11px 28px',
        borderRadius: 40,
        background: activeTab === id ? T.gradPrimary : 'transparent',
        color: activeTab === id ? T.white : T.textMid,
        border: `1px solid ${activeTab === id ? 'transparent' : T.borderLight}`,
        fontWeight: 600,
        fontSize: 14,
        cursor: 'pointer',
        transition: 'all 0.2s',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        fontFamily: "'Inter', sans-serif",
        boxShadow: activeTab === id ? T.shadow : 'none',
      }}
    >
      {label}
      {count !== undefined && (
        <span style={{
          background: activeTab === id ? 'rgba(255,255,255,0.2)' : T.borderLight,
          color: activeTab === id ? T.white : T.textMuted,
          borderRadius: 20,
          padding: '1px 8px',
          fontSize: 12,
          fontWeight: 700,
        }}>
          {count}
        </span>
      )}
    </button>
  );

  return (
    <div style={{ minHeight: '100vh', background: T.offWhite, fontFamily: "'Inter', sans-serif", color: T.text }}>
      {/* Font Import */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
      `}</style>

      {/* ── NAV ── */}
      <nav style={{
        padding: '20px 48px',
        borderBottom: `1px solid ${T.borderLight}`,
        background: T.white,
        position: 'sticky',
        top: 0,
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 16,
        boxShadow: T.shadow,
      }}>
        <Link to="/" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'baseline', gap: 2 }}>
          <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 900, background: T.gradPrimary, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
            Meeting
          </span>
          <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 900, color: T.text }}>
            21
          </span>
        </Link>
        <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
          <Link to="/events" style={{ color: T.textMuted, textDecoration: 'none', fontSize: 14, fontWeight: 500, transition: 'color 0.2s' }} onMouseEnter={e => e.currentTarget.style.color = T.primary} onMouseLeave={e => e.currentTarget.style.color = T.textMuted}>Events</Link>
          <Link to="/about" style={{ color: T.textMuted, textDecoration: 'none', fontSize: 14, fontWeight: 500, transition: 'color 0.2s' }} onMouseEnter={e => e.currentTarget.style.color = T.primary} onMouseLeave={e => e.currentTarget.style.color = T.textMuted}>About</Link>
          <Link to="/venue" style={{ color: T.textMuted, textDecoration: 'none', fontSize: 14, fontWeight: 500, transition: 'color 0.2s' }} onMouseEnter={e => e.currentTarget.style.color = T.primary} onMouseLeave={e => e.currentTarget.style.color = T.textMuted}>Venue</Link>
          <Link to="/agenda" style={{ color: T.primary, textDecoration: 'none', fontSize: 14, fontWeight: 600 }}>Agenda</Link>
          <Link to="/abstract" style={{ color: T.textMuted, textDecoration: 'none', fontSize: 14, fontWeight: 500, transition: 'color 0.2s' }} onMouseEnter={e => e.currentTarget.style.color = T.primary} onMouseLeave={e => e.currentTarget.style.color = T.textMuted}>Submit Abstract</Link>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section style={{
        textAlign: 'center',
        padding: '80px 24px 60px',
        position: 'relative',
        overflow: 'hidden',
        background: T.gradNavy,
      }}>
        <div style={{
          position: 'absolute',
          top: '-120px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 600,
          height: 600,
          background: 'radial-gradient(circle, rgba(59,130,246,0.15) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            background: 'rgba(255,255,255,0.12)',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 60,
            padding: '6px 18px',
            marginBottom: 28,
          }}
        >
          <RiCalendarLine size={14} color={T.accent} />
          <span style={{ fontSize: 11, fontWeight: 700, color: T.white, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Summit Schedule
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(44px, 6vw, 72px)',
            fontWeight: 900,
            lineHeight: 1.05,
            letterSpacing: '-0.02em',
            color: T.white,
            marginBottom: 16,
          }}
        >
          Event{' '}
          <span style={{ color: T.accent }}>Agenda</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          style={{ fontSize: 15, color: T.accent, maxWidth: 480, margin: '0 auto', lineHeight: 1.6 }}
        >
          Plan your summit experience with our comprehensive schedule of sessions and speakers.
        </motion.p>
      </section>

      {/* ── CONTENT ── */}
      <div style={{ maxWidth: 920, margin: '0 auto', padding: '52px 24px 80px' }}>

        {/* Stats row */}
        {!loading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 48 }}
          >
            <StatChip icon={RiCalendarLine} label="Total Sessions" value={summits.length} />
            <StatChip icon={RiCalendarEventLine} label="Upcoming" value={upcoming.length} />
            <StatChip icon={RiCheckLine} label="Completed" value={completed.length} />
          </motion.div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 36 }}>
          <TabBtn id="upcoming" label="Upcoming" count={!loading ? upcoming.length : undefined} />
          <TabBtn id="completed" label="Completed" count={!loading ? completed.length : undefined} />
        </div>

        {/* Sessions */}
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
            >
              <SectionLabel label={activeTab === 'upcoming' ? 'Upcoming Sessions' : 'Completed Sessions'} />
              {[1, 2, 3].map(i => <SkeletonCard key={i} />)}
            </motion.div>
          ) : activeTab === 'upcoming' ? (
            <motion.div
              key="upcoming"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.3 }}
            >
              <SectionLabel label="Upcoming Sessions" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {upcoming.length > 0
                  ? upcoming.map((session, i) => (
                      <SessionCard key={session._id} session={session} index={i} isCompleted={false} />
                    ))
                  : <EmptyState isCompleted={false} />
                }
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="completed"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.3 }}
            >
              <SectionLabel label="Completed Sessions" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {completed.length > 0
                  ? completed.map((session, i) => (
                      <SessionCard key={session._id} session={session} index={i} isCompleted={true} />
                    ))
                  : <EmptyState isCompleted={true} />
                }
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── FOOTER ── */}
      <footer style={{
        padding: '28px 48px',
        borderTop: `1px solid ${T.borderLight}`,
        background: T.white,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 16,
      }}>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 900 }}>
          <span style={{ background: T.gradPrimary, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Meeting</span>
          <span style={{ color: T.text }}>21</span>
        </div>
        <div style={{ fontSize: 12, color: T.textMuted }}>© 2025 Meeting21. All Rights Reserved.</div>
      </footer>
    </div>
  );
}